import React from 'react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './About.css';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const values = [
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
      </svg>
    ),
    title: 'Grown with Love',
    desc: 'Every flower is tended by hand, from seed to stem, with the care and attention it deserves.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
    title: 'Sustainably Farmed',
    desc: 'We use eco-friendly growing practices to protect the land we love.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    ),
    title: 'Community First',
    desc: 'From farm to table, we share beauty with our community through seasonal blooms and events.',
  },
];

const About = () => {
  return (
    <motion.main
      className="about"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header__bg">
          <img
            src="/background.png"
            alt="Cory Blossom flower farm"
          />
          <div className="page-header__overlay" />
        </div>
        <div className="page-header__content">
          <motion.span
            className="script-font page-header__eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Our Story
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            About Us
          </motion.h1>
        </div>
      </section>

      {/* Story Section */}
      <section className="about__story">
        <div className="container">
          <div className="about__story-grid">
            <AnimatedSection className="about__story-images" direction="left">
              <div className="about__img-stack">
                <motion.img
                  src="/background.png"
                  alt="Our Finger Lakes flower farm"
                  className="about__img-main"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                />
                <motion.img
                  src="/Wildflower_Sunrise.png"
                  alt="Wildflower arrangement"
                  className="about__img-accent"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </AnimatedSection>

            <AnimatedSection className="about__story-text" direction="right">
              <span className="script-font about__eyebrow">How it all began</span>
              <h2>From a Small Garden to a Finger Lakes Flower Farm</h2>
              <p>
                What started as a passion for growing beautiful flowers in our backyard 
                has blossomed into a thriving flower farm in the heart of New York's 
                Finger Lakes region. At Cory Blossom, we believe that flowers have the 
                power to brighten any day and transform any space.
              </p>
              <p>
                The Finger Lakes' rich soil and unique microclimate give our blooms 
                extraordinary color and longevity. From dahlias and sunflowers to roses 
                and wildflowers, we grow over 50 stunning varieties throughout the seasons.
              </p>
              <p>
                We're passionate about sharing the beauty of locally grown flowers with 
                our Finger Lakes community and beyond — whether it's for your kitchen table, 
                a vineyard wedding, a lakeside celebration, or just because.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Meet the Owner */}
      <section className="about__owner">
        <div className="container">
          <div className="about__owner-grid">
            <AnimatedSection className="about__owner-image" direction="left">
              <motion.img
                src="/coryowner.png"
                alt="Cory — Owner of Cory Blossom"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatedSection>
            <AnimatedSection className="about__owner-text" direction="right">
              <span className="script-font about__eyebrow">Meet the Grower</span>
              <h2>Hi, I'm Cory!</h2>
              <p>
                I'm the heart, hands, and soul behind Cory Blossom. What started as a small 
                passion project in my backyard has grown into a flower farm I pour my love into 
                every single day. From planting seeds in the spring to crafting arrangements 
                for Finger Lakes weddings and events, every bloom tells a story.
              </p>
              <p>
                I believe flowers have a magical way of connecting people — to nature, to 
                each other, and to the special moments that matter most. Whether you're 
                celebrating a milestone or just want to brighten an ordinary Tuesday, 
                I'm here to help you find the perfect blooms.
              </p>
              <p>
                When I'm not in the fields, you'll find me exploring the beautiful Finger 
                Lakes trails, visiting local vineyards, or dreaming up new arrangements 
                for the next season.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about__values">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">What We Stand For</h2>
            <p className="section-subtitle">Rooted in love, blooming with purpose</p>
          </AnimatedSection>

          <div className="about__values-grid">
            {values.map((val, i) => (
              <AnimatedSection key={val.title} delay={i * 0.15}>
                <motion.div
                  className="value-card"
                  whileHover={{ y: -6, boxShadow: '0 10px 40px rgba(0,0,0,0.12)' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="value-card__icon">{val.icon}</div>
                  <h3>{val.title}</h3>
                  <p>{val.desc}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Banner */}
      <section className="about__parallax">
        <motion.div
          className="about__parallax-bg"
          initial={{ scale: 1.1 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
        >
          <img
            src="/background.png"
            alt="Cory Blossom flower farm"
          />
          <div className="about__parallax-overlay" />
        </motion.div>
        <div className="about__parallax-content">
          <AnimatedSection>
            <h2>
              <span className="script-font">Sharing beauty</span><br />
              from the Finger Lakes to your hands
            </h2>
          </AnimatedSection>
        </div>
      </section>

      {/* Stats */}
      <section className="about__stats">
        <div className="container">
          <div className="about__stats-grid">
            {[
              { number: '5+', label: 'Years Growing' },
              { number: '50+', label: 'Flower Varieties' },
              // { number: '200+', label: 'Happy Customers' },
              { number: '100%', label: 'Locally Grown' },
            ].map((stat, i) => (
              <AnimatedSection key={stat.label} delay={i * 0.1}>
                <div className="stat-card">
                  <motion.span
                    className="stat-card__number"
                    initial={{ scale: 0.5 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1, type: 'spring', stiffness: 200 }}
                  >
                    {stat.number}
                  </motion.span>
                  <span className="stat-card__label">{stat.label}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </motion.main>
  );
};

export default About;
